import httpStore from "@/api/httpStore.js";

/**
 * Get pending products filtered by department and optionally by status and user
 * @param {Object} params - Query parameters
 * @param {string} params.departamento_id - Department ID (required)
 * @param {string} params.status - Filter by status: 'reported', 'discarded', or 'ordered' (optional)
 * @param {number} params.user_id - Filter by user ID (optional)
 * @returns {Promise}
 */
export function getPendingProducts(params) {
  return httpStore
    .get("pending_products", { params })
    .then(res => {
      return res.data;
    })
    .catch(e => {
      throw e;
    });
}

/**
 * Check if a product is already in pending products
 * @param {Object} params
 * @param {string} params.nkr_code - NKR product code (required)
 * @param {string} params.departamento_id - Department ID (required)
 * @param {string} params.status - Filter by status: 'reported', 'discarded', or 'ordered' (optional, default: 'reported')
 * @returns {Promise}
 */
export function checkPendingProduct(params) {
  return httpStore
    .get("pending_products/check", { params })
    .then(res => {
      return res.data;
    })
    .catch(e => {
      throw e;
    });
}

/**
 * Create a new pending product request
 * @param {Object} productData
 * @param {string} productData.nkr_code - NKR product code (required)
 * @param {string} productData.departamento_id - Department ID (required)
 * @param {number} productData.reported_quantity - reported quantity (optional, default: 1)
 * @param {number} productData.user_id - User ID (optional if in session)
 * @returns {Promise}
 */
export function createPendingProduct(productData) {
  return httpStore
    .post("pending_products", productData)
    .then(res => {
      return res.data;
    })
    .catch(e => {
      throw e;
    });
}

/**
 * Update a specific pending product by ID
 * @param {number} id - Pending product ID
 * @param {Object} updateData
 * @param {number} updateData.reported_quantity - Update reported quantity (optional)
 * @param {string} updateData.status - Update status: 'reported', 'discarded', or 'ordered' (optional)
 * @returns {Promise}
 */
export function updatePendingProduct(id, updateData) {
  return httpStore
    .put(`pending_products/${id}`, updateData)
    .then(res => {
      return res.data;
    })
    .catch(e => {
      throw e;
    });
}

/**
 * Update status for multiple pending products at once
 * @param {Object} bulkData
 * @param {Array<number>} bulkData.ids - Array of pending product IDs (required)
 * @param {string} bulkData.status - New status: 'reported', 'discarded', or 'ordered' (required)
 * @returns {Promise}
 */
export function bulkUpdatePendingProducts(bulkData) {
  return httpStore
    .put("pending_products/bulk_update", bulkData)
    .then(res => {
      return res.data;
    })
    .catch(e => {
      throw e;
    });
}

/**
 * Mark all pending products with a specific status to a new status
 * @param {Object} markAllData
 * @param {string} markAllData.from_status - Current status to filter: 'reported' or 'addressed' (required)
 * @param {string} markAllData.to_status - New status to set: 'reported' or 'addressed' (required)
 * @param {string} markAllData.departamento_id - Department ID (required)
 * @param {number} markAllData.user_id - Optional user ID filter (optional)
 * @returns {Promise}
 */
export function markAllPendingProducts(markAllData) {
  return httpStore
    .put("pending_products/mark_all", markAllData)
    .then(res => {
      return res.data;
    })
    .catch(e => {
      throw e;
    });
}

/**
 * Delete a specific pending product by ID
 * @param {number} id - Pending product ID
 * @returns {Promise}
 */
export function deletePendingProduct(id) {
  return httpStore
    .delete(`pending_products/${id}`)
    .then(res => {
      return res.data;
    })
    .catch(e => {
      throw e;
    });
}
